import {
  createStyles,
  SimpleGrid,
  Card,
  Image,
  Text,
  Container,
  AspectRatio,
  Title,
  Box,
  Group,
  Avatar,
  Button,
  rem,
} from "@mantine/core";
import { hostsDE, hostsEn, hostsNL } from "../../languages/hostPages";
import { HostHeader } from "./HostsHeader";
import { IconMapPin } from "@tabler/icons-react";

const useStyles = createStyles((theme) => ({
  card: {
    transition: "transform 150ms ease, box-shadow 150ms ease",

    "&:hover": {
      transform: "scale(1.01)",
      boxShadow: theme.shadows.md,
    },

    backgroundColor: "#FFF5F9",
  },

  title: {
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
    fontWeight: 600,
  },

  control: {
    "&:hover": {
      color: theme.white,
      backgroundColor: theme.colors.resirentPink[5],
    },

    border: `${rem(3)} solid ${
      theme.colorScheme === "dark"
        ? theme.colors.resirentPink[5]
        : theme.colors.resirentPink[8]
    }`,
    backgroundColor: theme.white,
    color: theme.colors.resirentPink[5],
    textAlign: "center", // Center-align the contents
  },
}));

export default function Hosts() {
  const { classes } = useStyles();
  const languagePrefix = window.location.pathname.startsWith("/nl")
    ? "/nl"
    : window.location.pathname.startsWith("/de")
    ? "/de"
    : "";
  const posts =
    languagePrefix === "/nl"
      ? hostsNL
      : languagePrefix === "/de"
      ? hostsDE
      : hostsEn;

  const content = {
    "/": {
      groupTitle: "EMPOWERING SMALL HOSTS",
      mainTitle:
        "Streamline Your Accommodation Experience: Discover All-in-One Solutions for Your Hosting Needs!",
      description:
        "At ResiRent, we help you to short let your accommodation sustainable and optimize your revenues. Our approach is rooted in the idea of being professional, independent and sustainable. Hop on board and get your short-term rental ready for the 21st century.",
    },
    "/nl": {
      groupTitle: "KLEINE GASTGEVERS VERSTERKEN",
      mainTitle:
        "Vereenvoudig uw accommodatie-ervaring: Ontdek alles-in-één oplossingen voor uw hostingbehoeften!",
      description:
        "Bij ResiRent helpen we u om uw accommodatie duurzaam te verhuren en uw inkomsten te optimaliseren. Onze aanpak is gebaseerd op het idee van professionaliteit, onafhankelijkheid en duurzaamheid. Stap aan boord en maak uw kortetermijnverhuur klaar voor de 21e eeuw.",
    },
    "/de": {
      groupTitle: "KLEINE GASTGEBER STÄRKEN",
      mainTitle:
        "Optimieren Sie Ihr Unterkunftserlebnis: Entdecken Sie All-in-One-Lösungen für Ihre Hosting-Bedürfnisse!",
      description:
        "Bei ResiRent helfen wir Ihnen, Ihre Unterkunft nachhaltig zu vermieten und Ihre Einnahmen zu maximieren. Unser Ansatz basiert auf den Prinzipien von Professionalität, Unabhängigkeit und Nachhaltigkeit. Steigen Sie ein und machen Sie Ihre Kurzzeitvermietung fit für das 21. Jahrhundert.",
    },
  };

  const cards = posts.map((article, index) => {
    const isEven = index % 2 === 0;
  
    return (
      <Box key={article.id} mb={80}>
        {/* === Accommodation Section (Alternating) === */}
        <SimpleGrid
          cols={2}
          spacing="xl"
          breakpoints={[{ maxWidth: "md", cols: 1 }]}
          style={{
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto",
            alignItems: "center",
          }}
        >
          {isEven ? (
            <>
              {/* Text Left */}
              <Box style={{ textAlign: "left" }}>
                <Group spacing={8} mb={4}>
                  <IconMapPin size={28} color="#003D51" />
                  <Text fw={500} size="md" style={{ color: "#003D51" }}>
                    {article.place}
                  </Text>
                </Group>
                <Title
                  order={1}
                  mb="sm"
                  sx={(theme) => ({
                    fontSize: 60,
                    fontWeight: 540,
                    [theme.fn.smallerThan("md")]: {
                      fontSize: 36,
                    },
                  })}
                >
                  {article.accommodationName}
                </Title>
                <Text
                  color="dimmed"
                  mb="lg"
                  mr="xl"
                  sx={(theme) => ({
                    fontSize: 20,
                    lineHeight: 1.5,
                    [theme.fn.smallerThan("md")]: {
                      fontSize: 16,
                    },
                  })}
                >
                  {article.description}
                </Text>
                <Button
                  radius="md"
                  component="a"
                  href={article.websiteUrl}
                  size="md"
                  style={{
                    backgroundColor: "#003D51",
                    color: "#fff",
                  }}
                >
                  {languagePrefix === "/nl"
                    ? "Bekijk hun website"
                    : languagePrefix === "/de"
                    ? "Visit the website"
                    : "Visit The Website"}
                </Button>
              </Box>
  
              {/* Image Right */}
              <AspectRatio ratio={16 / 10}>
                <Image
                  src={article.image}
                  style={{
                    borderRadius: "24px",
                    objectFit: "cover",
                  }}
                />
              </AspectRatio>
            </>
          ) : (
            <>
              {/* Image Left */}
              <AspectRatio ratio={16 / 10}>
                <Image
                  src={article.image}
                  style={{
                    borderRadius: "24px",
                    objectFit: "cover",
                  }}
                />
              </AspectRatio>
  
              {/* Text Right */}
              <Box style={{ textAlign: "left" }}>
                <Group spacing={8} mb={4}>
                  <IconMapPin size={28} color="#003D51" />
                  <Text fw={500} size="md" style={{ color: "#003D51" }}>
                    {article.place}
                  </Text>
                </Group>
                <Title
                  order={1}
                  mb="sm"
                  sx={(theme) => ({
                    fontSize: 60,
                    fontWeight: 540,
                    [theme.fn.smallerThan("md")]: {
                      fontSize: 36,
                    },
                  })}
                >
                  {article.accommodationName}
                </Title>
                <Text
                  color="dimmed"
                  mb="lg"
                  mr="xl"
                  sx={(theme) => ({
                    fontSize: 20,
                    lineHeight: 1.5,
                    [theme.fn.smallerThan("md")]: {
                      fontSize: 16,
                    },
                  })}
                >
                  {article.description}
                </Text>
                <Button
                  radius="md"
                  component="a"
                  href={article.websiteUrl}
                  size="md"
                  style={{
                    backgroundColor: "#003D51",
                    color: "#fff",
                  }}
                >
                  {languagePrefix === "/nl"
                    ? "Bekijk hun website"
                    : languagePrefix === "/de"
                    ? "Visit the website"
                    : "Visit The Website"}
                </Button>
              </Box>
            </>
          )}
        </SimpleGrid>
  
        {/* === Host Section (Alternating, Responsive) === */}
        <Box
          sx={(theme) => ({
            width: "100%",
            maxWidth: "1200px",
            margin: "40px auto 0 auto",
            backgroundColor: "#003D51",
            borderRadius: "24px",
            overflow: "hidden",
            display: "flex",
            flexDirection: isEven ? "row" : "row-reverse",
            height: 300,
            [theme.fn.smallerThan("md")]: {
              flexDirection: "column",
              height: "auto",
            },
          })}
        >
          {/* Host Image */}
          <Box
            sx={(theme) => ({
              flex: "1 1 10%",
              height: "100%",
              [theme.fn.smallerThan("md")]: {
                height: 200,
              },
            })}
          >
            <Image
              src={article.host.imageUrl}
              alt={article.host.name}
              height="100%"
              width="100%"
              fit="cover"
              style={{
                objectFit: "cover",
              }}
            />
          </Box>
  
          {/* Host Description */}
          <Box
            p="xl"
            sx={(theme) => ({
              flex: "1 1 60%",
              color: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              [theme.fn.smallerThan("md")]: {
                padding: theme.spacing.md,
              },
            })}
          >
            <Title
              order={2}
              mb="md"
              sx={(theme) => ({
                fontSize: 28,
                [theme.fn.smallerThan("md")]: {
                  fontSize: 20,
                },
              })}
            >
              {languagePrefix === "/nl"
                ? `Gastgever : ${article.host.name}`
                : languagePrefix === "/de"
                ? `Gastgeber : ${article.host.name}`
                : `Host : ${article.host.name}`}
            </Title>
            <Text
              sx={(theme) => ({
                fontSize: 17,
                lineHeight: 1.8,
                [theme.fn.smallerThan("md")]: {
                  fontSize: 14,
                },
              })}
            >
              {article.host.description}
            </Text>
          </Box>
        </Box>
      </Box>
    );
  });
  
  
  return (
    <>
      <HostHeader />
      <Box my={100} px="md">
        <SimpleGrid>{cards}</SimpleGrid>
      </Box>
    </>
  );
}
